// PendingDraftBanner — visas på mod-sidor när det finns ett sparat utkast efter
// att moderatorn har loggat in på nytt. Klick på „Återställ" återupprepar
// den ursprungliga API-förfrågan.
//
// Etappe 4.12 — Token-Expiry-skydd.
//
// Användning:
//   <PendingDraftBanner urlPattern="/api/mod/foreningar" onSuccess={() => refetch()} />
//
// urlPattern filtrerar så att bannern bara visas om utkastet hör till denna sida.
import { useEffect, useState } from "react";
import { RotateCcw, X } from "lucide-react";
import { readPendingDraft, clearPendingDraft, retryPendingDraft } from "@/utils/modAxios";

export default function PendingDraftBanner({ urlPattern, onSuccess }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const d = readPendingDraft();
    if (!d) return;
    if (urlPattern && !String(d.url || "").includes(urlPattern)) return;
    setDraft(d);
  }, [urlPattern]);

  if (!draft) return null;

  const handleRetry = async () => {
    setBusy(true);
    setError(null);
    const result = await retryPendingDraft();
    setBusy(false);
    if (result.ok) {
      setDraft(null);
      if (typeof onSuccess === "function") onSuccess();
    } else if (result.reason === "request-failed" && result.error?.isSessionExpired) {
      // Session-modalen tar över igen — banner döljs av sin egen logik.
      setDraft(null);
    } else {
      const detail = result.error?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sparningen misslyckades. Försök igen eller logga in på nytt.");
    }
  };

  const handleDismiss = () => {
    clearPendingDraft();
    setDraft(null);
  };

  const verb = draft.method === "DELETE" ? "raderingen" : draft.method === "POST" ? "skapandet" : "ändringen";
  const savedAt = (() => {
    try { return new Date(draft.savedAt).toLocaleString("sv-SE"); } catch { return ""; }
  })();

  return (
    <div
      data-testid="pending-draft-banner"
      className="mb-4 bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-start gap-3"
    >
      <RotateCcw size={18} className="text-amber-700 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink mb-1">
          Sparat utkast hittades
        </p>
        <p className="text-xs text-bark leading-relaxed">
          Vi har sparat {verb} från din förra session ({savedAt}). Klicka „Återställ" för att skicka den till servern igen.
        </p>
        {error && (
          <p data-testid="pending-draft-error" className="text-xs text-falu mt-2">{error}</p>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="pending-draft-retry"
            onClick={handleRetry}
            disabled={busy}
            className="inline-flex items-center gap-1.5 bg-pine hover:bg-pine-hover text-white text-xs font-semibold px-3 py-1.5 rounded-sm disabled:opacity-50"
          >
            <RotateCcw size={12} /> {busy ? "Återställer…" : "Återställ och spara"}
          </button>
          <button
            type="button"
            data-testid="pending-draft-dismiss"
            onClick={handleDismiss}
            className="inline-flex items-center gap-1.5 text-bark hover:text-ink text-xs font-medium px-3 py-1.5 rounded-sm border border-warm-border"
          >
            <X size={12} /> Avfärda utkastet
          </button>
        </div>
      </div>
    </div>
  );
}
